import AcknowledgementNotice from 'components/AcknowledgementNotice/AcknowledgementNotice';
import CustomButton from 'tailwind-components/Button/Button';
import FileUpload from 'components/FileUpload/FileUpload';
import Modal from 'tailwind-components/Modal/Modal';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import styles from './RequiredActionsModal.module.scss';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { isDateValid } from 'helpers/appointments/appointments';
import SuncareContractSigningStep from 'components/SuncareContractSigningStep/SuncareContractSigningStep';
import SuncareTrainingNotice from 'components/SuncareTrainingNotice/SuncareTrainingNotice';

import {
  getCurrentAction,
  isStepValid
} from 'helpers/requiredActions/requiredActions';

import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';

const KAISER_GA_CHECKBOX_LABEL_EN =
  'I acknowledge that I have at least one active sold Kaiser Permanente policy in Georgia and will provide the required policyholder information to complete the appointment application.';

const KAISER_GA_CHECKBOX_LABEL_ES =
  'Reconozco que cuento con al menos una póliza activa vendida de Kaiser Permanente en Georgia y que tengo la información requerida del titular de la póliza, incluyendo nombre, fecha de nacimiento y número de Seguro Social, para completar la solicitud de appointment.';

export default function RequiredActionsModal({
  isLoading,
  isOpen,
  closeModal,
  requiredActions,
  handleFilesChange,
  formData,
  handleClick,
  handleExpirationDatesChange,
  setAcknowledgement = () => {},
  userEmail = '',
  isSuncareContractStep = false,
  onSendContract = () => {}
}) {
  const [specialAcknowledgement, setSpecialAcknowledgement] = useState(false);
  const [kaiserAckLanguage, setKaiserAckLanguage] = useState('en');
  const [currentIndex, setCurrentIndex] = useState(
    () => getCurrentAction(requiredActions, formData) // gets first invalid action index or last action if all are valid
  );

  const isCurrentStepValid = isStepValid(
    currentIndex,
    requiredActions[currentIndex],
    formData,
    specialAcknowledgement
  );

  const canSendRequest = requiredActions.every((_, index) =>
    isStepValid(index, requiredActions[index], formData, specialAcknowledgement)
  );

  const handleNext = () => {
    // only allow moving to the next step if the current step is valid
    if (isCurrentStepValid && currentIndex < requiredActions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // determine if there's an error with the current date
  const currentExpirationDate = formData.expirationDates[currentIndex];
  const currentFiles = formData.files[currentIndex] || [];
  const hasFiles = Array.isArray(currentFiles) && currentFiles.length > 0;
  const kaiserGaName = formData?.kaiserGaPolicyholderFullName ?? '';
  const kaiserGaDob = formData?.kaiserGaPolicyholderDob ?? null;
  const kaiserGaSsn = formData?.kaiserGaPolicyholderSsn ?? '';

  // get the date validation results
  const dateValidation = !!currentExpirationDate
    ? isDateValid(currentExpirationDate.$d)
    : { valid: true, message: '' };

  // determine error message for the date field
  let dateError = '';

  // if the current action has an expiration date
  if (
    requiredActions[currentIndex]?.expires &&
    !requiredActions[currentIndex]?.defaultExpirationDate
  ) {
    const action = requiredActions[currentIndex];

    // if required and has no date
    if (action?.required && !currentExpirationDate) {
      dateError = 'An expiration date is required for this document.';
    }
    // if date is invalid, show the validation message
    else if (currentExpirationDate && !dateValidation.valid) {
      dateError = dateValidation.message;
    }
    // if not required but has files without date
    else if (!action?.required && hasFiles && !currentExpirationDate) {
      dateError = 'An expiration date is required when a file is uploaded.';
    }
  }

  return (
    <Modal
      active={isOpen}
      title={isLoading ? 'Sending Request...' : 'Required Actions'}
      size={'larger'}
      closeModal={isLoading ? () => {} : closeModal}
      showScrollbar
    >
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='space-between'
        height='100%'
      >
        <Box
          flexGrow={1}
          display='flex'
          flexDirection='column'
          alignItems='center'
        >
          <Typography variant='h5' gutterBottom>
            {currentIndex + 1} of {requiredActions.length}
          </Typography>

          <Typography variant='h6' gutterBottom className={styles.uploadTitle}>
            {requiredActions[currentIndex]?.title}{' '}
            {requiredActions[currentIndex]?.state && (
              <>
                for{' '}
                <span className={styles.stateText}>
                  {requiredActions[currentIndex]?.state}
                </span>
              </>
            )}
          </Typography>

          {requiredActions[currentIndex]?.type === 'acknowledgement' && (
            <AcknowledgementNotice
              message={requiredActions[currentIndex]?.description}
              isError={!formData?.acknowledgement}
              checked={formData?.acknowledgement}
              onChange={value => setAcknowledgement('acknowledgement', value)}
              variant='info'
            />
          )}

          {requiredActions[currentIndex]?.type ===
            'providenceAcknowledgement' && (
            <AcknowledgementNotice
              message={requiredActions[currentIndex]?.description}
              isError={!formData?.providenceAcknowledgement}
              checked={formData?.providenceAcknowledgement}
              onChange={value =>
                setAcknowledgement('providenceAcknowledgement', value)
              }
              variant='info'
            />
          )}

          {requiredActions[currentIndex]?.type === 'bcbsNcAcknowledgement' && (
            <AcknowledgementNotice
              message={requiredActions[currentIndex]?.description}
              isError={!formData?.bcbsNcAcknowledgement}
              checked={formData?.bcbsNcAcknowledgement}
              onChange={value =>
                setAcknowledgement('bcbsNcAcknowledgement', value)
              }
              variant='info'
            />
          )}

          {requiredActions[currentIndex]?.type === 'bcbsScAcknowledgement' && (
            <AcknowledgementNotice
              message={requiredActions[currentIndex]?.description}
              isError={!formData?.bcbsScAcknowledgement}
              checked={formData?.bcbsScAcknowledgement}
              onChange={value =>
                setAcknowledgement('bcbsScAcknowledgement', value)
              }
              variant='info'
            />
          )}

          {requiredActions[currentIndex]?.type ===
            'kaiserGaSoldPolicyAcknowledgement' && (
            <Box sx={{ width: '99%' }}>
              <Tabs
                value={kaiserAckLanguage}
                onChange={(_, value) => setKaiserAckLanguage(value)}
                aria-label='Notice language'
                sx={{
                  alignSelf: 'flex-start',
                  minHeight: 40,
                  mb: 1,
                  '& .MuiTab-root': { minHeight: 40, textTransform: 'none' }
                }}
              >
                <Tab label='English' value='en' />
                <Tab label='Español' value='es' />
              </Tabs>
              <AcknowledgementNotice
                message={
                  kaiserAckLanguage === 'en'
                    ? requiredActions[currentIndex]?.descriptionEn ??
                      requiredActions[currentIndex]?.description
                    : requiredActions[currentIndex]?.descriptionEs ??
                      requiredActions[currentIndex]?.description
                }
                isError={!formData?.kaiserGaSoldPolicyAcknowledgement}
                checked={!!formData?.kaiserGaSoldPolicyAcknowledgement}
                onChange={value =>
                  setAcknowledgement('kaiserGaSoldPolicyAcknowledgement', value)
                }
                checkboxLabel={
                  <Typography component='span' variant='body2' display='block'>
                    {kaiserAckLanguage === 'en'
                      ? KAISER_GA_CHECKBOX_LABEL_EN
                      : KAISER_GA_CHECKBOX_LABEL_ES}
                  </Typography>
                }
                variant='info'
              />
              {!!formData?.kaiserGaSoldPolicyAcknowledgement && (
                <>
                  <Typography variant='body2' sx={{ mt: 2 }}>
                    Please provide the following information to complete the
                    appointment application:
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, minmax(0, 1fr))'
                      },
                      gap: 2
                    }}
                  >
                    <TextField
                      label="Policyholder's full name"
                      value={kaiserGaName}
                      onChange={event =>
                        setAcknowledgement(
                          'kaiserGaPolicyholderFullName',
                          event.target.value
                        )
                      }
                      required
                      fullWidth
                      error={!kaiserGaName.trim()}
                      helperText={
                        !kaiserGaName.trim()
                          ? "Policyholder's full name is required."
                          : ''
                      }
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Policyholder's date of birth (DOB)"
                        value={kaiserGaDob}
                        onChange={date =>
                          setAcknowledgement('kaiserGaPolicyholderDob', date)
                        }
                        disableFuture
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            required: true,
                            fullWidth: true,
                            error: !kaiserGaDob,
                            helperText: !kaiserGaDob
                              ? "Policyholder's date of birth is required."
                              : ''
                          }
                        }}
                      />
                    </LocalizationProvider>
                    <TextField
                      label="Policyholder's Social Security number"
                      value={kaiserGaSsn}
                      onChange={event =>
                        setAcknowledgement(
                          'kaiserGaPolicyholderSsn',
                          event.target.value
                        )
                      }
                      required
                      fullWidth
                      error={!kaiserGaSsn.trim()}
                      helperText={
                        !kaiserGaSsn.trim()
                          ? "Policyholder's Social Security number is required."
                          : ''
                      }
                    />
                  </Box>
                </>
              )}
            </Box>
          )}

          {requiredActions[currentIndex]?.type === 'suncareContractSigning' && (
            <SuncareContractSigningStep
              formData={formData}
              setAcknowledgement={setAcknowledgement}
              userEmail={userEmail}
            />
          )}

          {requiredActions[currentIndex]?.type === 'suncareTrainingNotice' && (
            <SuncareTrainingNotice userEmail={userEmail} />
          )}

          {requiredActions[currentIndex]?.type === 'fileUpload' && (
            <FileUpload
              files={formData?.files?.[currentIndex] || []}
              onFilesChange={handleFilesChange}
              accept={requiredActions[currentIndex]?.accept}
              title={requiredActions[currentIndex]?.title}
              description={requiredActions[currentIndex]?.description}
              url={requiredActions[currentIndex]?.url}
              transferNote={requiredActions[currentIndex]?.transferNote}
              fileIndex={currentIndex}
              required={requiredActions[currentIndex]?.required}
              expirationDate={formData?.expirationDates?.[currentIndex]}
              isDateValid={isDateValid}
              disabled={isLoading}
              isError={
                // 1. required and no files
                (requiredActions[currentIndex]?.required &&
                  (!currentFiles || currentFiles.length === 0)) ||
                // 2. has expires flag, has files but no valid date
                (requiredActions[currentIndex]?.expires &&
                  hasFiles &&
                  (!currentExpirationDate || !dateValidation.valid)) ||
                // 3. has expires flag, has date but no files
                (requiredActions[currentIndex]?.expires &&
                  currentExpirationDate &&
                  !hasFiles)
              }
              acknowledgementMessage={
                requiredActions[currentIndex]?.acknowledgementMessage
              }
              specialAcknowledgement={specialAcknowledgement}
              setSpecialAcknowledgement={setSpecialAcknowledgement}
            />
          )}

          {requiredActions[currentIndex]?.expires &&
            !requiredActions[currentIndex]?.defaultExpirationDate && (
              <Box
                flexGrow={1}
                display='flex'
                flexDirection='column'
                alignItems='center'
                width='100%'
                sx={{ mt: 2 }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='Expiration Date'
                    value={formData.expirationDates?.[currentIndex]}
                    onChange={date =>
                      handleExpirationDatesChange(currentIndex, date)
                    }
                    minDate={dayjs()}
                    disablePast
                    renderInput={params => <TextField {...params} fullWidth />}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!dateError
                      }
                    }}
                    disabled={isLoading}
                  />
                </LocalizationProvider>
                {!!dateError && (
                  <FormHelperText
                    error
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  >
                    {dateError}
                  </FormHelperText>
                )}
              </Box>
            )}
        </Box>

        {isLoading &&
          requiredActions.some(({ type }) => type === 'fileUpload') && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              flexDirection='column'
              width='100%'
              mt={2}
            >
              <Alert severity='info' sx={{ mb: 2, width: '100%' }}>
                <Typography variant='body1' fontWeight='bold' gutterBottom>
                  Uploading files. Please don&apos;t close or refresh this page.
                </Typography>
                <Typography variant='body2'>
                  This process may take a few moments depending on your internet
                  connection and file size. If something goes wrong, you&apos;ll
                  see an error message.
                </Typography>
              </Alert>
            </Box>
          )}

        <Box display='flex' justifyContent='space-between' width='100%' mt={2}>
          <Button
            onClick={handlePrev}
            disabled={currentIndex === 0 || isLoading}
          >
            Previous
          </Button>
          {currentIndex < requiredActions.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!isCurrentStepValid || isLoading}
            >
              Next
            </Button>
          ) : (
            <CustomButton
              disabled={!canSendRequest || isLoading}
              isLoading={isLoading}
              size='small'
              variant='primaryLight'
              text={isSuncareContractStep ? 'Send Contract' : 'Send request'}
              rounded='small'
              fill
              style={{ width: 'fit-content' }}
              onClick={isSuncareContractStep ? onSendContract : handleClick}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
}

RequiredActionsModal.propTypes = {
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  requiredActions: PropTypes.array,
  handleFilesChange: PropTypes.func,
  formData: PropTypes.object,
  handleClick: PropTypes.func,
  handleExpirationDatesChange: PropTypes.func,
  setAcknowledgement: PropTypes.func,
  userEmail: PropTypes.string,
  isSuncareContractStep: PropTypes.bool,
  onSendContract: PropTypes.func
};
